import { usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"

import type { FlashData } from "@/types"

function showFlash(flash: FlashData) {
  if (flash.alert) toast.error(flash.alert)
  if (flash.notice) toast(flash.notice)
}

/**
 * Identity of a flash's CONTENT, or null when there is nothing to show.
 *
 * Content, not object identity: a background partial reload
 * (`router.reload({ only: [...] })`) hands the hook a fresh object carrying
 * the SAME already-shown message. The server does not re-send the flash on a
 * partial reload (verified) — what arrives is the stale client-side copy — so
 * object identity changes on every poll while the message does not.
 */
export function flashKey(flash: FlashData | null | undefined): string | null {
  const alert = flash?.alert ?? ""
  const notice = flash?.notice ?? ""
  if (!alert && !notice) return null
  return `${alert}||${notice}`
}

/**
 * Should this flash be toasted, given the last one we showed?
 *
 * Returns the key to remember, or null to stay silent. An empty flash resets
 * the memo, so the same message after any other navigation shows again.
 *
 * The cost of content-based dedupe is that the same message twice in a row
 * surfaces as one toast. The alternative is a message that never stops:
 * without this, every page with a periodic poll re-toasted its last flash
 * forever — "2 documents uploaded. Processing..." reappearing every 60s long
 * after the upload had finished.
 */
export function nextFlashKey(
  flash: FlashData | null | undefined,
  lastShown: string | null,
): { show: boolean; key: string | null } {
  const key = flashKey(flash)
  if (key === null) return { show: false, key: null }
  if (key === lastShown) return { show: false, key: lastShown }
  return { show: true, key }
}

/**
 * The effect body, extracted so the ORDERING can be tested.
 *
 * The whole bug this guards against is *when* the memo is written, and that is
 * invisible to a test of `nextFlashKey` alone — there is no DOM environment
 * here to render the hook in. Taking `memo` and `schedule` as parameters lets a
 * test drive the real StrictMode sequence (setup -> cleanup -> setup) against
 * the real code.
 *
 * Returns the cleanup, or undefined when nothing was scheduled.
 */
export function runFlashEffect(
  flash: FlashData | null | undefined,
  memo: { current: string | null },
  schedule: (fire: () => void) => () => void,
  show: (flash: FlashData) => void = showFlash,
): (() => void) | undefined {
  const { show: shouldShow, key } = nextFlashKey(flash, memo.current)
  if (!shouldShow) {
    // Still record it, so an EMPTY flash clears the memo.
    memo.current = key
    return undefined
  }

  // The memo is written when the toast actually FIRES, never when it is merely
  // scheduled. React StrictMode runs setup -> cleanup -> setup, so recording it
  // at schedule time marked the cleared first attempt as "already shown" and
  // the remount then suppressed it as a duplicate — no toast appeared at all.
  return schedule(() => {
    memo.current = key
    if (flash) show(flash)
  })
}

export function useFlash() {
  const { flash } = usePage()
  const lastShown = useRef<string | null>(null)

  // setTimeout + cleanup is what makes StrictMode's setup -> cleanup -> setup
  // fire exactly one toast.
  useEffect(
    () =>
      runFlashEffect(flash, lastShown, (fire) => {
        const timeout = setTimeout(fire, 0)
        return () => clearTimeout(timeout)
      }),
    [flash],
  )
}
